// Every data point extracted for the dossier at import, from QRT cells or from
// the text of narrative documents: N, N-1, change, where each value comes from,
// and the chapters and sections it is routed to. Exportable to Excel.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { CompareTable, Variation, formatAmount } from "../components/drafting";
import type { DatapointItem } from "../api/types";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { useDossier } from "../dossier";
import { locale, w } from "../i18n";

export function DatapointsPage() {
  const { dossier, inspect } = useDossier();
  const lang = locale();
  const data = useResource(`datapoints:${dossier.id}:${lang}`, () => api.datapoints(dossier.id, lang));
  const [template, setTemplate] = useState("all");
  const [chapter, setChapter] = useState("all");
  const items = (data.data?.items || []).filter(i => (template === "all" || i.template === template)
    && (chapter === "all" || i.chapters.includes(chapter)));
  const templates = [...new Set((data.data?.items || []).map(i => i.template).filter(Boolean))] as string[];
  const chapters = [...new Set((data.data?.items || []).flatMap(i => i.chapters))].sort();

  return <>
    <PageHeader title={w("dp.title")} lead={w("dp.lead")}
      actions={<a className="ws-btn primary" href={api.datapointsExportUrl(dossier.id, lang)} download>
        <Download size={16} aria-hidden="true" />{w("dp.export")}</a>} />
    {data.error && <ErrorState title={w("common.genericError")} error={data.error} onRetry={data.reload} />}
    {data.loading && <SkeletonBlock lines={10} />}
    {data.data && <>
      <section className="ws-card ws-steps ws-steps-5" aria-label={w("dp.summary")}>
        {([["found_n", data.data.counts.found_n], ["found_n1", data.data.counts.found_n1], ["with_change", data.data.counts.with_change],
          ["from_text", data.data.counts.from_text], ["indicators", data.data.counts.indicators]] as const).map(([key, value]) => <div key={key} className="ws-step">
          <div className="ws-step-value">{value}</div><div className="ws-step-label">{w(`dp.kpi.${key}`)}</div></div>)}
      </section>
      {data.data.counts.found_n1 === 0 && <div style={{ marginTop: 12 }}><Notice tone="caution">{w("dp.noPrior")}</Notice></div>}
      <div className="ws-row" style={{ margin: "16px 0 12px" }}>
        <label className="ws-small ws-muted" htmlFor="f-template">{w("dp.template")}</label>
        <select id="f-template" className="ws-select" value={template} onChange={e => setTemplate(e.target.value)}>
          <option value="all">{w("docs.filter.all")}</option>{templates.map(t => <option key={t} value={t}>{t}</option>)}</select>
        <label className="ws-small ws-muted" htmlFor="f-chapter">{w("dp.chapter")}</label>
        <select id="f-chapter" className="ws-select" value={chapter} onChange={e => setChapter(e.target.value)}>
          <option value="all">{w("docs.filter.all")}</option>{chapters.map(c => <option key={c} value={c}>{c}</option>)}</select>
      </div>
      <div className="ws-card ws-table-wrap"><table className="ws-table ws-audit-table">
        <thead><tr><th>{w("table.indicator")}</th><th>{w("dp.datapoint")}</th><th className="num">N-1</th><th className="num">N</th>
          <th className="num">{w("table.var")}</th><th>{w("dp.source")}</th><th>{w("dp.usedBy")}</th></tr></thead>
        <tbody>{items.map(item => <tr key={item.indicator}>
          <td><strong>{item.label}</strong>{item.unit_assumed && <div className="ws-small ws-faint">{w("audits.unitAssumed")}</div>}</td>
          <td className="ws-mono ws-small">{item.datapoint}</td>
          <td className="num">{item.n1 == null ? w("table.na") : formatAmount(item.n1, item.unit)}</td>
          <td className="num">{item.n == null ? w("table.na") : formatAmount(item.n, item.unit)}</td>
          <td className="num"><Variation delta={item.delta} deltaUnit={item.delta_unit} variation={item.variation} /></td>
          <td className="ws-small">
            {item.document_n_id && <button className="ws-link" onClick={() => inspect(item.document_n_id!)}>N : {item.document_n}</button>}
            {item.method_n && <MethodTag method={item.method_n} />}
            {item.source_n && <div className="ws-faint ws-mono">{item.source_n}</div>}
            {item.document_n1_id && <button className="ws-link" onClick={() => inspect(item.document_n1_id!)}>N-1 : {item.document_n1}</button>}
            {item.method_n1 && <MethodTag method={item.method_n1} />}
            {item.source_n1 && <div className="ws-faint ws-mono">{item.source_n1}</div>}
            {item.confidence && <StatusBadge status={item.confidence === "eleve" ? "ready" : item.confidence === "moyen" ? "partial" : "needs_review"}
              label={w(`confidence.${item.confidence}`)} subtle />}</td>
          <td className="ws-small">{item.used_by.map(code => <Link key={code} className="ws-tag" style={{ marginRight: 4 }}
            to={`/dossiers/${dossier.id}/sections/${code}`}>{code}</Link>)}</td>
        </tr>)}</tbody></table></div>
      {data.data.notes.length > 0 && <div style={{ marginTop: 12 }}><Notice tone="neutral" title={w("dp.notes")}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>{data.data.notes.map((note, i) => <li key={i} className="ws-small">{note}</li>)}</ul></Notice></div>}
      <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("dp.note")}</Notice></div>
    </>}
  </>;
}

function MethodTag({ method }: { method: "qrt" | "text" }) {
  return <span className="ws-tag" style={{ marginLeft: 4 }}>{w(`dp.method.${method}`)}</span>;
}

/** The data points routed to one chapter, for the chapter page. */
export function ChapterDatapoints({ chapter }: { chapter: string }) {
  const { dossier } = useDossier();
  const lang = locale();
  const data = useResource(`datapoints:${dossier.id}:${lang}`, () => api.datapoints(dossier.id, lang));
  const items: DatapointItem[] = (data.data?.items || []).filter(i => i.chapters.includes(chapter));
  return <section className="ws-card" style={{ marginBottom: 16 }}>
    <div className="ws-card-head"><h2>{w("dp.chapterTitle")}</h2>
      <Link className="ws-link ws-small" to={`/dossiers/${dossier.id}/datapoints`}>{w("dp.viewAll")}</Link></div>
    {data.loading && <SkeletonBlock lines={3} />}
    {data.error && <ErrorState title={w("common.genericError")} error={data.error} onRetry={data.reload} />}
    {data.data && items.length === 0 && <div style={{ padding: "0 16px 16px" }}><Notice tone="neutral">{w("dp.chapterEmpty")}</Notice></div>}
    {items.length > 0 && <div className="ws-table-wrap"><CompareTable rows={items.map(item => ({
      key: item.indicator, unit: item.unit, n: item.n, n1: item.n1, delta: item.delta, deltaUnit: item.delta_unit,
      variation: item.variation, hint: item.datapoint, sourceN: item.source_n, sourceN1: item.source_n1,
      label: <>{item.label}{item.method_n === "text" && <MethodTag method="text" />}</>,
      extra: item.used_by.filter(code => code.startsWith(`${chapter}.`)).map(code =>
        <Link key={code} className="ws-tag" style={{ marginRight: 4 }} to={`/dossiers/${dossier.id}/sections/${code}`}>{code}</Link>),
    }))} /></div>}
  </section>;
}
